'use client';

import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { Plus, Search, Filter, Upload, Eye, Edit3, X, CheckCircle, Phone, MapPin, Briefcase, Star, FileText } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

const formatVT = (n: number) => `VT ${n.toLocaleString('en-US')}`;

interface Borrower {
  id: string;
  full_name: string;
  phone: string;
  national_id: string;
  address: string;
  occupation: string;
  employer: string;
  guarantor_name: string;
  status: string;
  risk_rating: string;
  date_registered: string;
}

const getRiskColor = (rating: string) => {
  if (rating === 'Low') return 'text-positive';
  if (rating === 'Medium') return 'text-warning';
  return 'text-negative';
};

const getRiskScore = (rating: string) => {
  if (rating === 'Low') return 85;
  if (rating === 'Medium') return 65;
  if (rating === 'High') return 45;
  return 25;
};

const getStatusStyle = (status: string) => {
  if (status === 'Active' || status === 'Good Standing') return 'bg-positive/10 text-positive';
  if (status === 'Defaulted' || status === 'High Risk') return 'bg-negative/10 text-negative';
  return 'bg-muted text-muted-foreground';
};

type ModalType = 'add' | 'profile' | null;

export default function BorrowersPage() {
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState<ModalType>(null);
  const [selected, setSelected] = useState<Borrower | null>(null);
  const [borrowers, setBorrowers] = useState<Borrower[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', phone: '', idNumber: '', address: '', occupation: '', employer: '', emergency: '' });

  const supabase = createClient();

  const fetchBorrowers = useCallback(async () => {
    setLoading(true);
    const { data, error: fetchError } = await supabase
      .from('borrowers')
      .select('*')
      .order('created_at', { ascending: false });
    if (fetchError) {
      console.error('Fetch borrowers error:', fetchError.message);
    } else {
      setBorrowers(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchBorrowers(); }, [fetchBorrowers]);

  const filtered = borrowers.filter(b =>
    b.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    b.phone?.includes(search) ||
    b.national_id?.includes(search)
  );

  const openProfile = (b: Borrower) => { setSelected(b); setModal('profile'); };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Full name is required'); return; }
    setSaving(true);
    setError(null);
    const { error: insertError } = await supabase
      .from('borrowers')
      .insert({
        full_name: form.name.trim(),
        phone: form.phone.trim(),
        national_id: form.idNumber.trim(),
        address: form.address.trim(),
        occupation: form.occupation.trim(),
        employer: form.employer.trim(),
        guarantor_name: form.emergency.trim(),
        status: 'Active',
        risk_rating: 'Low',
      });
    setSaving(false);
    if (insertError) {
      setError(insertError.message);
    } else {
      setForm({ name: '', phone: '', idNumber: '', address: '', occupation: '', employer: '', emergency: '' });
      setModal(null);
      fetchBorrowers();
    }
  };

  const activeCount = borrowers.filter(b => b.status === 'Active' || b.status === 'Good Standing').length;
  const overdueCount = borrowers.filter(b => b.status === 'Defaulted' || b.status === 'High Risk').length;

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Borrowers</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Client database with KYC verification vault</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => { setError(null); setForm({ name: '', phone: '', idNumber: '', address: '', occupation: '', employer: '', emergency: '' }); setModal('add'); }} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">
              <Plus size={16} /> Add New Borrower
            </button>
            <button className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors">
              <Upload size={16} /> Upload KYC Document
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Borrowers', value: borrowers.length, sub: 'registered clients', color: 'text-info' },
            { label: 'Active', value: activeCount, sub: 'with active loans', color: 'text-positive' },
            { label: 'High Risk / Defaulted', value: overdueCount, sub: 'need follow-up', color: 'text-negative' },
            { label: 'Avg Risk', value: borrowers.length > 0 ? Math.round(borrowers.reduce((s, b) => s + getRiskScore(b.risk_rating), 0) / borrowers.length) : 0, sub: 'portfolio health', color: 'text-warning' },
          ].map(stat => (
            <div key={stat.label} className="bg-card border border-border rounded-xl p-4">
              <p className="text-xs text-muted-foreground font-medium mb-1">{stat.label}</p>
              <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{stat.sub}</p>
            </div>
          ))}
        </div>

        {/* Search & Filter */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, phone, ID..." className="w-full pl-9 pr-4 py-2 rounded-lg bg-card border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
          <button className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border text-sm text-muted-foreground hover:text-foreground transition-colors">
            <Filter size={15} /> Filter
          </button>
        </div>

        {/* Borrowers Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">Loading borrowers...</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <p className="text-sm">No borrowers found.</p>
            <button onClick={() => setModal('add')} className="mt-3 text-primary text-sm hover:underline">+ Add your first borrower</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map(b => (
              <div key={b.id} className="bg-card border border-border rounded-xl p-4 hover:border-primary/30 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/15 border border-primary/20 flex items-center justify-center shrink-0">
                      <span className="text-sm font-bold text-primary">{b.full_name?.split(' ').map((n: string) => n[0]).join('') || '?'}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">{b.full_name}</p>
                      <p className="text-xs text-muted-foreground">{b.national_id || 'No ID'}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${getStatusStyle(b.status)}`}>{b.status}</span>
                </div>

                <div className="space-y-1.5 mb-3">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Phone size={12} /> {b.phone || '—'}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <MapPin size={12} /> {b.address || '—'}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Briefcase size={12} /> {b.occupation || '—'} · {b.employer || '—'}
                  </div>
                </div>

                {/* Risk Rating */}
                <div className="flex items-center justify-between mb-3 p-2 rounded-lg bg-muted/30">
                  <div className="flex items-center gap-2">
                    <Star size={13} className={getRiskColor(b.risk_rating)} />
                    <span className="text-xs font-medium text-muted-foreground">Risk</span>
                  </div>
                  <span className={`text-xs font-bold ${getRiskColor(b.risk_rating)}`}>{b.risk_rating} Risk</span>
                </div>

                <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                  <span>Joined {b.date_registered}</span>
                  <span className="flex items-center gap-1"><FileText size={11} /> KYC</span>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <button onClick={() => openProfile(b)} className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg bg-muted/50 hover:bg-muted text-xs font-medium text-foreground transition-colors">
                    <Eye size={13} /> View Profile
                  </button>
                  <button className="flex items-center justify-center px-2 py-1.5 rounded-lg bg-muted/50 hover:bg-muted text-xs font-medium text-muted-foreground transition-colors">
                    <Edit3 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card z-10">
              <h3 className="font-bold text-foreground">
                {modal === 'add' && '+ Add New Borrower'}
                {modal === 'profile' && `${selected?.full_name} — Profile`}
              </h3>
              <button onClick={() => setModal(null)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground"><X size={16} /></button>
            </div>

            {modal === 'add' && (
              <div className="p-6 space-y-4">
                {error && <div className="p-3 rounded-lg bg-negative/10 border border-negative/20 text-negative text-sm">{error}</div>}
                {[
                  { label: 'Full Name *', key: 'name', placeholder: 'Borrower full name' },
                  { label: 'Phone Number', key: 'phone', placeholder: '+678 555 0000' },
                  { label: 'ID Number', key: 'idNumber', placeholder: 'VU-ID-000000' },
                  { label: 'Address', key: 'address', placeholder: 'Port Vila, Efate' },
                  { label: 'Occupation', key: 'occupation', placeholder: 'e.g. Market Vendor' },
                  { label: 'Employer', key: 'employer', placeholder: 'e.g. Self-employed' },
                  { label: 'Emergency Contact / Guarantor', key: 'emergency', placeholder: 'Name & phone' },
                ].map(f => (
                  <div key={f.key}>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1.5">{f.label}</label>
                    <input value={(form as any)[f.key]} onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))} placeholder={f.placeholder} className="w-full px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                  </div>
                ))}
                <div className="flex gap-3 pt-2">
                  <button onClick={() => setModal(null)} className="flex-1 px-4 py-2 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:bg-muted transition-colors">Cancel</button>
                  <button onClick={handleSave} disabled={saving} className="flex-1 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                    {saving ? 'Saving...' : <><CheckCircle size={15} /> Save Borrower</>}
                  </button>
                </div>
              </div>
            )}

            {modal === 'profile' && selected && (
              <div className="p-6 space-y-4">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-muted/30">
                  <div className="w-14 h-14 rounded-full bg-primary/15 border-2 border-primary/30 flex items-center justify-center">
                    <span className="text-xl font-bold text-primary">{selected.full_name?.split(' ').map((n: string) => n[0]).join('') || '?'}</span>
                  </div>
                  <div>
                    <p className="font-bold text-foreground text-lg">{selected.full_name}</p>
                    <p className="text-sm text-muted-foreground">{selected.national_id || 'No ID'}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${getStatusStyle(selected.status)}`}>{selected.status}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Phone', value: selected.phone || '—' },
                    { label: 'Address', value: selected.address || '—' },
                    { label: 'Occupation', value: selected.occupation || '—' },
                    { label: 'Employer', value: selected.employer || '—' },
                    { label: 'Registered', value: selected.date_registered },
                    { label: 'Guarantor', value: selected.guarantor_name || '—' },
                  ].map(item => (
                    <div key={item.label} className="p-3 rounded-lg bg-muted/30">
                      <p className="text-xs text-muted-foreground mb-0.5">{item.label}</p>
                      <p className="text-sm font-medium text-foreground">{item.value}</p>
                    </div>
                  ))}
                </div>
                <div className="p-3 rounded-lg bg-muted/30">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-muted-foreground">Risk Rating</span>
                    <span className={`text-sm font-bold ${getRiskColor(selected.risk_rating)}`}>{selected.risk_rating} Risk</span>
                  </div>
                </div>
                <button onClick={() => setModal(null)} className="w-full px-4 py-2 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:bg-muted transition-colors">Close</button>
              </div>
            )}
          </div>
        </div>
      )}
    </AppLayout>
  );
}
